import React from 'react';
import { Box, Switch, Typography } from '@material-ui/core';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_ONBOARDING_OPTION_STAGES } from '../../../../../common/onboarding-option-task/onboarding-option-stages-model.gql';
import { GetOnboardingOptionStages } from '../../../../../common/onboarding-option-task/__generated__/GetOnboardingOptionStages';

interface OnboardingOptionRequiredToggleProps {
  optionType: string;
  optionLabel: string;
  enabled: boolean;
}

export const OnboardingOptionRequiredToggle: React.FC<OnboardingOptionRequiredToggleProps> = ({
  optionType,
  optionLabel,
  enabled,
}) => {
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const requiredMutation = useCampfireFetch({ defer: true });

  const { data, refetch } = useCampfireQuery<GetOnboardingOptionStages, {}>(GET_ONBOARDING_OPTION_STAGES);
  const isRequired =
    data?.vm.onboardingOptionStages.find((stage) => stage.optionType === optionType)?.isRequired ?? false;

  const handleToggle = async () => {
    try {
      const response = await requiredMutation.run({
        url: `${apiUrl}/vm/admin-console/onboarding-option-stage/required`,
        method: 'post',
        data: { optionType, isRequired: !isRequired },
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSnackbar({
        variant: 'success',
        open: true,
        message: `${optionLabel} is now ${isRequired ? 'optional' : 'required'}`,
      });
      refetch();
    } catch (error) {
      setSnackbar({ variant: 'error', open: true, message: `Couldn't update ${optionLabel}` });
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <Box display='flex' alignItems='flex-start' style={{ marginTop: '20px', marginLeft: '16px' }}>
      <Switch
        color='primary'
        size='small'
        checked={isRequired}
        disabled={requiredMutation.isLoading}
        onChange={handleToggle}
        inputProps={{ 'aria-label': `${optionLabel} required` }}
      />
      <Box ml={1}>
        <Typography variant='subtitle2' style={{ fontSize: '14px', color: '#666', fontWeight: 600 }}>
          Required
        </Typography>
        <Typography style={{ fontSize: '12px', color: '#666' }}>
          Show this in Required Tasks. Applicants can&apos;t submit their profile until it&apos;s complete.
        </Typography>
      </Box>
    </Box>
  );
};
